import React, { useState, useEffect } from "react";
import FlightItem from "./FlightItem";
import { airlineLogos } from "../../constants/constant";
import { Box, Modal, Pagination, Typography } from "@mui/material";
import FlightRoundTripCard from "./FlightRoundTripCard";
import {
  formatDate,
  isToday,
  parseCustomDateString,
} from "../../constants/helper";
import FlightDetailRoundTripCard from "./FlightDetailRoundTripCard";
import FlightLoader from "../basicComponent/FlightLoader";
import { passenger } from "../../constants/types";
import { useCommission } from "../../services/customHooks/useCommission";
import IntlRTFLightListCard from "../../screens/flight/IntlRTFLightListCard";

type FlightListMainCardProps = {
  flightData: any;
  loading: boolean;
  origin: string;
  destination: string;
  tripType: number;
  selectedDepartureDate: string | null;
  setSelectedDepartureDate: (date?: string) => void;
  selectedReturnDate?: string | null;
  setSelectedReturnDate: (date?: string) => void;
  travellersDetail: passenger;
  isInternationalFlight?: boolean;
  traceId?: string;
  cabin: number;
  ruleId?: string;
};

const FlightListMainCard = ({
  traceId,
  isInternationalFlight,
  flightData,
  loading,
  destination,
  origin,
  tripType,
  selectedDepartureDate,
  selectedReturnDate,
  setSelectedDepartureDate,
  setSelectedReturnDate,
  travellersDetail,
  cabin,
  ruleId,
}: FlightListMainCardProps) => {
  const [itemsPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<"cheapest" | "fastest">("cheapest");
  // Separate state for outbound and inbound data
  const [outboundData, setOutboundData] = useState<any>(null);
  const [inboundData, setInboundData] = useState<any>(null);
  // Current paginated items
  const [currentOutboundItems, setCurrentOutboundItems] = useState<any[]>([]);
  const [currentInboundItems, setCurrentInboundItems] = useState<any[]>([]);

  const [selectedOutboundFlightId, setSelectedOutboundFlightId] = useState<
    string | null
  >(null);
  const [selectedInboundFlightId, setSelectedInboundFlightId] = useState<
    string | null
  >(null);
  const [selectedInboundData, setSelectedInboundData] = useState();
  const [selectedOutboundData, setSelectedOutboundData] = useState();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedOutBoundItem, setSelectedOutBoundItem] = useState({
    origin: "",
    destination: "",
    departureTime: "",
    arrivalTime: "",
    fare: 0,
    flightName: "",
    flightCode: "",
    airlineName: "",
  });

  const { commission: outBoundCommission } = useCommission(
    selectedOutboundData,
    travellersDetail
  );

  const [selectedInBoundItem, setSelectedInBoundItem] = useState({
    origin: "",
    destination: "",
    departureTime: "",
    arrivalTime: "",
    fare: 0,
    flightName: "",
    flightCode: "",
    airlineName: "",
  });
  const { commission: inboundCommission } = useCommission(
    selectedInboundData,
    travellersDetail
  );
  // Reset to page 1 when flightData changes
  useEffect(() => {
    setCurrentPage(1);
  }, [flightData]);

  // Helper function to sort flight segments
  const sortFlightSegments = (
    segments: any[],
    sortType: "cheapest" | "fastest"
  ) => {
    if (!segments || !Array.isArray(segments)) return segments;

    const getMinutes = (timeStr: string): number => {
      if (!timeStr) return 0;
      const hours = parseInt(timeStr.match(/(\d+)h/)?.[1] || "0");
      const minutes = parseInt(timeStr.match(/(\d+)m/)?.[1] || "0");
      return hours * 60 + minutes;
    };

    return [...segments].sort((a, b) => {
      if (sortType === "cheapest") {
        return (
          (a.Fare?.TotalFareWithOutMarkUp || 0) -
          (b.Fare?.TotalFareWithOutMarkUp || 0)
        );
      } else {
        // For fastest, sort by journey time
        const aTime = a.Bonds?.[0]?.JourneyTime || "";
        const bTime = b.Bonds?.[0]?.JourneyTime || "";
        return getMinutes(aTime) - getMinutes(bTime);
      }
    });
  };
  // First separate the data into outbound and inbound and apply initial sorting
  useEffect(() => {
    if (!flightData) {
      setOutboundData(null);
      setInboundData(null);
      return;
    }

    console.log("Flight Data:", JSON.stringify(flightData));

    // Handle the case where flightData is an array
    if (Array.isArray(flightData)) {
      if (tripType === 0) {
        // One-way trip
        const sortedOutbound = {
          ...flightData[0],
          Segments: sortFlightSegments(
            flightData[0]?.Segments || [],
            "cheapest"
          ),
        };
        setOutboundData(sortedOutbound);
        setInboundData(null);
      } else if (tripType === 1) {
        // Round trip
        if (isInternationalFlight) {
          // International round trip - single object with outbound/inbound bonds
          if (flightData[0]?.Segments) {
            const outboundSegments = flightData[0].Segments.filter(
              (segment: any) =>
                segment?.Bonds?.some(
                  (bond: any) => bond?.BoundType === "OutBound"
                )
            );
            const inboundSegments = flightData[0].Segments.filter(
              (segment: any) =>
                segment?.Bonds?.some(
                  (bond: any) => bond?.BoundType === "InBound"
                )
            );

            const sortedOutbound = {
              ...flightData[0],
              Segments: sortFlightSegments(outboundSegments, "cheapest"),
            };

            const sortedInbound = {
              ...flightData[0],
              Segments: sortFlightSegments(inboundSegments, "cheapest"),
            };

            setOutboundData(sortedOutbound);
            setInboundData(sortedInbound);
          }
        } else {
          // Domestic round trip - separate objects for outbound and inbound
          const sortedOutbound = {
            ...flightData[0],
            Segments: sortFlightSegments(
              flightData[0]?.Segments || [],
              "cheapest"
            ),
          };

          const sortedInbound = flightData[1]
            ? {
                ...flightData[1],
                Segments: sortFlightSegments(
                  flightData[1]?.Segments || [],
                  "cheapest"
                ),
              }
            : null;

          setOutboundData(sortedOutbound);
          setInboundData(sortedInbound);
        }
      }
    } else {
      // Handle non-array data (fallback)
      setOutboundData(null);
      setInboundData(null);
    }
  }, [flightData, tripType, isInternationalFlight]);

  // Re-sort data whenever sortBy changes
  useEffect(() => {
    if (outboundData?.Segments && Array.isArray(outboundData.Segments)) {
      const sortedOutboundSegments = sortFlightSegments(
        outboundData.Segments,
        sortBy
      );
      setOutboundData((prev: any) => ({
        ...prev,
        Segments: sortedOutboundSegments,
      }));
    }

    if (inboundData?.Segments && Array.isArray(inboundData.Segments)) {
      const sortedInboundSegments = sortFlightSegments(
        inboundData.Segments,
        sortBy
      );
      setInboundData((prev: any) => ({
        ...prev,
        Segments: sortedInboundSegments,
      }));
    }
  }, [sortBy]);

  // Then paginate the separated data
  useEffect(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;

    // Handle outbound pagination - FIXED
    if (outboundData?.Segments && Array.isArray(outboundData.Segments)) {
      const paginatedOutbound = outboundData.Segments.slice(
        startIdx,
        startIdx + itemsPerPage
      );

      // For domestic round trip, ensure minimum 4 items for outbound as well
      let outboundResult = [...paginatedOutbound];
      if (
        !isInternationalFlight &&
        tripType === 1 &&
        outboundResult.length > 0 &&
        outboundResult.length < 4
      ) {
        for (let i = outboundResult.length; i < 4; i++) {
          const lastItem = { ...outboundResult[outboundResult.length - 1] };
          lastItem.tempId = `outbound-copy-${i}`;
          outboundResult.push(lastItem);
        }
      }

      setCurrentOutboundItems(outboundResult);
    } else {
      setCurrentOutboundItems([]);
    }

    // Handle inbound pagination
    if (inboundData?.Segments && Array.isArray(inboundData.Segments)) {
      const paginatedInbound = inboundData.Segments.slice(
        startIdx,
        startIdx + itemsPerPage
      );

      // For domestic round trip, ensure minimum 4 items for UI consistency
      let inboundResult = [...paginatedInbound];
      if (
        !isInternationalFlight &&
        tripType === 1 &&
        inboundResult.length > 0 &&
        inboundResult.length < 4
      ) {
        for (let i = inboundResult.length; i < 4; i++) {
          const lastItem = { ...inboundResult[inboundResult.length - 1] };
          lastItem.tempId = `inbound-copy-${i}`;
          inboundResult.push(lastItem);
        }
      }

      setCurrentInboundItems(inboundResult);
    } else {
      setCurrentInboundItems([]);
    }
  }, [
    outboundData,
    inboundData,
    currentPage,
    itemsPerPage,
    isInternationalFlight,
    tripType,
  ]);

  // Add these console logs right after your pagination useEffect to debug
  useEffect(() => {
    console.log("=== PAGINATION DEBUG ===");
    console.log("Trip Type:", tripType);
    console.log("Is International:", isInternationalFlight);

    console.log("Current Outbound Items Length:", currentOutboundItems);
    console.log("Current Inbound Items Length:", currentInboundItems);

    console.log("========================");
  }, [
    currentPage,
    currentOutboundItems,
    currentInboundItems,
    outboundData,
    inboundData,
  ]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  // Calculate total pages based on the segment with the most items
  const calculateTotalPages = () => {
    const outboundCount = outboundData?.Segments?.length || 0;
    const inboundCount = inboundData?.Segments?.length || 0;

    // For round trip, use the maximum count of either outbound or inbound
    // For one-way, use outbound count
    const maxCount =
      tripType === 1 ? Math.max(outboundCount, inboundCount) : outboundCount;

    return Math.max(1, Math.ceil(maxCount / itemsPerPage));
  };

  // Check if we have any data to display
  const hasData = (): boolean => {
    const outboundLength = outboundData?.Segments?.length || 0;
    const inboundLength = inboundData?.Segments?.length || 0;

    if (tripType === 0) {
      return outboundLength > 0;
    } else {
      // For round trip, we need at least outbound data
      return outboundLength > 0;
    }
  };
  const makeRowId = (
    direction: "outbound" | "inbound",
    page: number,
    index: number
  ) => `${direction}-${page}-${index}`;

  function setPreviousDate(selectedDate?: string | Date | null) {
    // Convert selectedDate to a Date object if it's a string
    if (typeof selectedDate === "string") {
      const [year, month, day] = selectedDate.split("-").map(Number);
      selectedDate = new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
    }

    // Ensure selectedDate is a valid date
    if (!!selectedDate && isNaN(selectedDate.getTime())) {
      console.error("Invalid date provided");
      return;
    }

    // Create a new date object for the previous day
    const previousDate = new Date(!!selectedDate ? selectedDate : new Date());

    previousDate.setDate(previousDate.getDate() - 1);

    // Format date as DD-MM-YYYY
    const formattedPreviousDate = formatDate(previousDate);

    return formattedPreviousDate;
  }

  // Reusable function for updating the date
  function updateDate(selectedDate?: string | Date | null): string | undefined {
    let dateObj: Date;

    if (typeof selectedDate === "string") {
      // Parse ISO string
      dateObj = new Date(selectedDate);
    } else if (selectedDate instanceof Date) {
      dateObj = new Date(selectedDate);
    } else {
      dateObj = new Date(); // fallback to today
    }

    if (isNaN(dateObj.getTime())) {
      console.error("Invalid date provided:", selectedDate);
      return;
    }

    // Add 1 day
    dateObj.setDate(dateObj.getDate() + 1);

    // Format to YYYY-MM-DD
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  }

  useEffect(() => {
    setExpandedId(null);
    setSelectedOutboundFlightId(null);
    setSelectedInboundFlightId(null);
  }, [currentPage, flightData, sortBy]);

  // Function to handle flight selection
  const handleFlightSelect = (
    flightId: string,
    direction: "outbound" | "inbound",
    data: any
  ) => {
    if (direction === "outbound") {
      setSelectedOutboundFlightId(flightId);
      setSelectedOutBoundItem({
        origin: origin,
        destination: destination,
        departureTime: data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime,
        arrivalTime:
          data?.Bonds?.[0]?.Legs?.[data?.Bonds?.[0]?.Legs?.length - 1]
            .ArrivalTime,
        fare: data?.Fare?.TotalFareWithOutMarkUp,
        flightName: data?.Bonds?.[0]?.Legs?.[0]?.FlightName,
        flightCode:
          data?.Bonds?.[0]?.Legs?.[0]?.AirlineName +
          "-" +
          data?.Bonds?.[0]?.Legs?.[0]?.FlightNumber,
        airlineName: data?.Bonds?.[0]?.Legs?.[0]?.AirlineName,
      });
      setSelectedOutboundData(data);
    } else {
      setSelectedInboundFlightId(flightId);
      // Use different bond index based on flight type
      setSelectedInBoundItem({
        origin: destination,
        destination: origin,
        departureTime: data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime,
        arrivalTime:
          data?.Bonds?.[0]?.Legs?.[data?.Bonds?.[0]?.Legs?.length - 1]
            .ArrivalTime,
        fare: data?.Fare?.TotalFareWithOutMarkUp,
        flightName: data?.Bonds?.[0]?.Legs?.[0]?.FlightName,
        flightCode:
          data?.Bonds?.[0]?.Legs?.[0]?.AirlineName +
          "-" +
          data?.Bonds?.[0]?.Legs?.[0]?.FlightNumber,
        airlineName: data?.Bonds?.[0]?.Legs?.[0]?.AirlineName,
      });
      setSelectedInboundData(data);
    }
  };

  return (
    <div>
      <div className="container flight-list-mainCard">
        <div className="sort-bar mb-n2">
          <div className="d-flex align-items-center">
            <div className="flight-list-font">Sort By:</div>
            <div>
              <button
                className={sortBy === "cheapest" ? "active mx-2" : "mx-2 "}
                onClick={() => setSortBy("cheapest")}
              >
                <i className="fa-solid fa-arrow-down-short-wide px-1"></i>
                CHEAPEST
              </button>
              <button
                className={sortBy === "fastest" ? "active mx-2" : "mx-2"}
                onClick={() => setSortBy("fastest")}
              >
                <i className="fa-regular fa-clock px-1"></i>FASTEST
              </button>
            </div>
          </div>

          {tripType === 0 && (
            <div className="prev-nxt-btn">
              {!isToday(selectedDepartureDate) && (
                <button
                  className="txt-m1 px-1 flight-list-font cursor-pointer"
                  onClick={() =>
                    setSelectedDepartureDate(
                      setPreviousDate(selectedDepartureDate)
                    )
                  }
                  disabled={selectedDepartureDate === formatDate(new Date())}
                >
                  Previous Day |
                </button>
              )}

              <button
                className="txt-m1 px-1 flight-list-font cursor-pointer"
                onClick={() =>
                  setSelectedDepartureDate(updateDate(selectedDepartureDate))
                }
              >
                Next Day
              </button>
            </div>
          )}
        </div>

        {tripType === 0 ? (
          // One-way  trip display
          <>
            {loading ? (
              <FlightLoader />
            ) : currentOutboundItems.length > 0 ? (
              currentOutboundItems.map((data: any, index: number) => (
                <FlightItem
                  key={data.tempId || index}
                  flightData={data}
                  isActive={expandedId === index.toString()}
                  onToggle={() =>
                    setExpandedId((prev: string | null) =>
                      prev === index.toString() ? null : index.toString()
                    )
                  }
                  destination={destination}
                  flightCode={
                    data?.Bonds?.[0]?.Legs?.[0]?.AirlineName +
                    "-" +
                    data?.Bonds?.[0]?.Legs?.[0]?.FlightNumber
                  }
                  flightName={data?.Bonds?.[0]?.Legs?.[0]?.FlightName}
                  flightReachTime={
                    data?.Bonds?.[data?.Bonds?.length - 1]?.Legs?.[
                      data?.Bonds?.[data?.Bonds?.length - 1]?.Legs?.length - 1
                    ]?.ArrivalTime ?? null
                  }
                  flightTakeoffTime={data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime}
                  origin={origin}
                  stop={data?.Bonds?.[0]?.Legs?.length - 1}
                  totalFare={data?.Fare?.TotalFareWithOutMarkUp}
                  totaltime={data?.Bonds?.[0]?.JourneyTime}
                  flightImg={
                    airlineLogos[data?.Bonds?.[0]?.Legs?.[0]?.AirlineName]
                  }
                  ArrivalDate={data?.Bonds?.[0]?.Legs?.[0]?.ArrivalDate}
                  ArrivalTerminal={data?.Bonds?.[0]?.Legs?.[0]?.ArrivalTerminal}
                  ArrivalTime={data?.Bonds?.[0]?.Legs?.[0]?.ArrivalTime}
                  cabinBaggage={data?.Bonds?.[0]?.Legs?.[0]?.CabinBagWT}
                  checkInBaggageWeight={
                    data?.Bonds?.[0]?.Legs?.[0]?.BaggageWeight
                  }
                  departureDate={data?.Bonds?.[0]?.Legs?.[0]?.DepartureDate}
                  departureTerminal={
                    data?.Bonds?.[0]?.Legs?.[0]?.DepartureTerminal
                  }
                  departureTime={data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime}
                  paxfare={data?.Fare?.PaxFares}
                  travellersDetail={travellersDetail}
                  traceId={traceId}
                  cabin={cabin}
                />
              ))
            ) : (
              <div className="row justify-content-center mt-5 ">
                <div className="col-md-5">
                  <div className="">
                    <div className="flex justify-center items-center bg-gray-50 mt-3">
                      <div className="flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full">
                        <img
                          src="media/images/remove-folder.png"
                          alt="remove-folder"
                        />
                      </div>
                    </div>

                    <div className="no-flights-message p-2 text-center">
                      <h3 className="no-data-head">
                        No flights found matching your filters
                      </h3>
                      <p className="text-secondary text-sm">
                        Try adjusting your filter criteria to see more results
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          // Round-trip display
          <>
            {loading ? (
              <FlightLoader />
            ) : hasData() && !isInternationalFlight ? (
              <div className="row ">
                {/* Outbound Flights Section */}
                {currentOutboundItems?.length > 0 ? (
                  <div className="col-md-6 blockContainer">
                    <div className="n-w-ron-bg margin10 d-flex justify-content-between">
                      <div className="">
                        <div className="travel-det pdt5">
                          <span className="ng-binding">{origin}</span> →{" "}
                          <span className="ng-binding">{destination}</span>{" "}
                        </div>
                      </div>
                      <div className="">
                        <div className="day-date pdt5 ng-binding text-right">
                          {(() => {
                            const parsed = parseCustomDateString(
                              currentOutboundItems?.[0]?.Bonds?.[0]?.Legs?.[0]
                                ?.DepartureDate
                            );
                            return parsed
                              ? `${parsed.day}, ${parsed.date} ${parsed.month} ${parsed.year}`
                              : "";
                          })()}
                        </div>
                      </div>
                      {/* <div className="col-md-4 text-right">
                        {!isToday(selectedDepartureDate) && (
                          <button
                            className="txt-m1 px-1 flight-list-font cursor-pointer"
                            onClick={() =>
                              setSelectedDepartureDate(
                                setPreviousDate(selectedDepartureDate)
                              )
                            }
                            disabled={
                              selectedDepartureDate === formatDate(new Date())
                            }
                          >
                            Previous Day |
                          </button>
                        )}

                        <button
                          className="txt-m1 px-1 flight-list-font cursor-pointer"
                          onClick={() =>
                            setSelectedDepartureDate(
                              updateDate(selectedDepartureDate)
                            )
                          }
                        >
                          Next Day
                        </button>
                      </div> */}
                    </div>
                    {currentOutboundItems?.map((data: any, index: number) => {
                      const rowId = makeRowId("outbound", currentPage, index); // ✅ unique id
                      return (
                        <FlightRoundTripCard
                          key={index}
                          isIntl={isInternationalFlight}
                          flightData={data} // Pass complete outbound data
                          journeyData={data} // Pass specific journey segment
                          destination={destination}
                          flightCode={
                            data?.Bonds?.[0]?.Legs?.[0]?.AirlineName +
                            "-" +
                            data?.Bonds?.[0]?.Legs?.[0]?.FlightNumber
                          }
                          flightName={data?.Bonds?.[0]?.Legs?.[0]?.FlightName}
                          flightReachTime={
                            data?.Bonds?.[data?.Bonds?.length - 1]?.Legs?.[
                              data?.Bonds?.[data?.Bonds?.length - 1]?.Legs
                                ?.length - 1
                            ]?.ArrivalTime ?? null
                          }
                          flightTakeoffTime={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime
                          }
                          origin={origin}
                          stop={data?.Bonds?.[0]?.Legs?.length - 1}
                          totalFare={data?.Fare?.TotalFareWithOutMarkUp}
                          totaltime={data?.Bonds?.[0]?.JourneyTime}
                          flightImg={
                            airlineLogos[
                              data?.Bonds?.[0]?.Legs?.[0]?.AirlineName
                            ]
                          }
                          cabinBaggage={data?.Bonds?.[0]?.Legs?.[0]?.CabinBagWT}
                          checkInBaggageWeight={
                            data?.Bonds?.[0]?.Legs?.[0]?.BaggageWeight
                          }
                          departureDate={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureDate
                          }
                          departureTerminal={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureTerminal
                          }
                          departureTime={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime
                          }
                          paxfare={data?.Fare?.PaxFares}
                          flightId={rowId}
                          onFlightSelect={() =>
                            handleFlightSelect(rowId, "outbound", data)
                          }
                          selectedFlightId={selectedOutboundFlightId || ""}
                          direction="outbound"
                          traceId={traceId}
                          travellerDetail={travellersDetail}
                          isExpanded={expandedId === rowId}
                          onToggleExpand={() =>
                            setExpandedId((prev: string | null) =>
                              prev === rowId ? null : rowId
                            )
                          }
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="col-sm-6">
                    <div className="no-flights-message p-4 text-center">
                      <h3>No outbound flights found</h3>
                      <p>
                        Try adjusting your filter criteria to see more results.
                      </p>
                    </div>
                  </div>
                )}

                {/* Inbound Flights Section - Show only if inbound data exists */}
                {inboundData &&
                currentInboundItems.length > 0 &&
                !isInternationalFlight ? (
                  <div className="col-md-6">
                    <div className="n-w-ron-bg margin10 d-flex justify-content-between">
                      <div className="">
                        <div className="travel-det pdt5">
                          <span className="ng-binding">{destination}</span> →{" "}
                          <span className="ng-binding">{origin}</span>{" "}
                        </div>
                      </div>
                      <div className="">
                        <div className="day-date pdt5 ng-binding text-right">
                          {(() => {
                            const parsed = parseCustomDateString(
                              currentInboundItems?.[0]?.Bonds?.[0]?.Legs?.[0]
                                ?.DepartureDate
                            );
                            return parsed
                              ? `${parsed.day}, ${parsed.date} ${parsed.month} ${parsed.year}`
                              : "";
                          })()}
                        </div>
                      </div>
                      {/* <div className="col-md-4 text-right">
                        {selectedReturnDate !== null &&
                          selectedReturnDate !== undefined &&
                          selectedDepartureDate !== null &&
                          selectedDepartureDate !== undefined &&
                          !isToday(selectedReturnDate) &&
                          selectedReturnDate !== selectedDepartureDate && ( // hide if equal
                            <button
                              className="txt-m1 px-1 flight-list-font cursor-pointer"
                              onClick={() =>
                                setSelectedReturnDate(
                                  setPreviousDate(selectedReturnDate)
                                )
                              }
                              disabled={
                                isToday(selectedReturnDate) ||
                                selectedReturnDate === selectedDepartureDate
                              }
                            >
                              Previous Day |
                            </button>
                          )}

                        <button
                          className="txt-m1 px-1 flight-list-font cursor-pointer"
                          onClick={() =>
                            setSelectedReturnDate(
                              updateDate(selectedReturnDate)
                            )
                          }
                        >
                          Next Day
                        </button>
                      </div> */}
                    </div>
                    {currentInboundItems.map((data: any, index: number) => {
                      const rowId = makeRowId("inbound", currentPage, index); // ✅
                      return (
                        <FlightRoundTripCard
                          key={data.tempId || `inbound-${index}`}
                          isIntl={isInternationalFlight}
                          flightData={data} // Pass complete inbound data
                          journeyData={data} // Pass specific journey segment
                          destination={origin} // Swapped for inbound
                          flightCode={
                            data?.Bonds?.[0]?.Legs?.[0]?.AirlineName +
                            "-" +
                            data?.Bonds?.[0]?.Legs?.[0]?.FlightNumber
                          }
                          flightName={data?.Bonds?.[0]?.Legs?.[0]?.FlightName}
                          flightReachTime={
                            data?.Bonds?.[data?.Bonds?.length - 1]?.Legs?.[
                              data?.Bonds?.[data?.Bonds?.length - 1]?.Legs
                                ?.length - 1
                            ]?.ArrivalTime ?? null
                          }
                          flightTakeoffTime={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime
                          }
                          origin={destination} // Swapped for inbound
                          stop={data?.Bonds?.[0]?.Legs?.length - 1}
                          totalFare={data?.Fare?.TotalFareWithOutMarkUp}
                          totaltime={data?.Bonds?.[0]?.JourneyTime}
                          flightImg={
                            airlineLogos[
                              data?.Bonds?.[0]?.Legs?.[0]?.AirlineName
                            ]
                          }
                          cabinBaggage={data?.Bonds?.[0]?.Legs?.[0]?.CabinBagWT}
                          checkInBaggageWeight={
                            data?.Bonds?.[0]?.Legs?.[0]?.BaggageWeight
                          }
                          departureDate={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureDate
                          }
                          departureTerminal={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureTerminal
                          }
                          departureTime={
                            data?.Bonds?.[0]?.Legs?.[0]?.DepartureTime
                          }
                          paxfare={data?.Fare?.PaxFares}
                          flightId={rowId}
                          onFlightSelect={() =>
                            handleFlightSelect(rowId, "inbound", data)
                          }
                          selectedFlightId={selectedInboundFlightId || ""}
                          direction="inbound"
                          traceId={traceId}
                          travellerDetail={travellersDetail}
                          isExpanded={expandedId === rowId} // ✅
                          onToggleExpand={() =>
                            setExpandedId((prev) =>
                              prev === rowId ? null : rowId
                            )
                          }
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="col-md-6">
                    <div className="no-flights-message p-4 text-center">
                      <h3>No inbound flights found</h3>
                      <p>
                        Try adjusting your filter criteria to see more results.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : hasData() && isInternationalFlight ? (
              <div className="row">
                {/* Outbound Flights Section */}
                {currentOutboundItems?.length > 0 ? (
                  currentOutboundItems.map((data: any, index: number) => (
                    <IntlRTFLightListCard
                      flightData={data}
                      travellersDetail={travellersDetail}
                      isActive={expandedId === index.toString()}
                      onToggle={() =>
                        setExpandedId((prev: string | null) =>
                          prev === index.toString() ? null : index.toString()
                        )
                      }
                      traceId={traceId}
                      inboundCommission={inboundCommission}
                      outboundCommission={outBoundCommission}
                    />
                  ))
                ) : (
                  <div className="col-md-6">
                    <div className="no-flights-message p-4 text-center">
                      <h3>No outbound flights found</h3>
                      <p>
                        Try adjusting your filter criteria to see more results.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="row justify-content-center mt-5 ">
                <div className="col-md-5">
                  <div className="">
                    <div className="flex justify-center items-center bg-gray-50 mt-3">
                      <div className="flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full">
                        <img
                          src="media/images/remove-folder.png"
                          alt="remove-folder"
                        />
                      </div>
                    </div>

                    <div className="no-flights-message p-2 text-center">
                      <h3 className="no-data-head">
                        No flights found matching your filters
                      </h3>
                      <p className="text-secondary text-sm">
                        Try adjusting your filter criteria to see more results
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!loading && hasData() && (
          <div className="row">
            <div className="col-md-12 d-flex justify-content-end mt-4 mb-2 flight-pagination">
              <Pagination
                count={calculateTotalPages()}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </div>
          </div>
        )}

        {tripType === 1 &&
          !!selectedInboundFlightId &&
          !!selectedOutboundFlightId && (
            <FlightDetailRoundTripCard
              inBoundItem={selectedInBoundItem}
              outBoundItem={selectedOutBoundItem}
              inBoundData={selectedInboundData}
              outBoundData={selectedOutboundData}
              travellersDetail={travellersDetail}
              traceId={traceId}
              ruleId={ruleId} //TODO:anjali
              inboundCommission={inboundCommission}
              outboundCommission={outBoundCommission}
            />
          )}
      </div>
    </div>
  );
};

export default FlightListMainCard;
